import type { ValidationErrors } from '../../../../../utils/validation';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import {
    Policies,
    TfcV1DiagnosticReportBezoekAanSpecialist,
} from '../../../../../../common/validation/customControls/ophthalmology';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import {
    ButtonContainer,
    Form,
    FormDivider,
    HeaderDivider,
    InputCheckbox,
    InputTextArea,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { InputControl } from '../../../../elements/forms/inputControl/InputControl';
import { InputTextCheckbox } from '../../../../elements/InputTextCheckbox';
import { RadioGroupField } from '../../../../fields/RadioGroup';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { OldFundusscreeningPolicyAndUrgencyAddEditFields } from '../../TFO/v1/OldFundusscreeningPolicyAndUrgencyAddEditFields';
import { DiagnosisConclusionSelection } from '../../utils/v1/DiagnosisConclusionSelection';

interface IInnerTfcV1DiagnosticReportAddEditState {
    buttonDisabled?: boolean;
    isAndersTextBevindingenChecked: boolean;
}

export interface IBevindingen {
    andersText: string | null;
    andersText_od: boolean;
    andersText_os: boolean;
    bloedinkjes: boolean;
    bloedinkjes_od: boolean;
    bloedinkjes_os: boolean;
    cotton_wool_spots: boolean;
    cotton_wool_spots_od: boolean;
    cotton_wool_spots_os: boolean;
    exsudaten: boolean;
    exsudaten_od: boolean;
    exsudaten_os: boolean;
    geen_afwijkingen: boolean;
    geen_afwijkingen_od: boolean;
    geen_afwijkingen_os: boolean;
    niet_te_beoordelen: boolean;
    niet_te_beoordelen_od: boolean;
    niet_te_beoordelen_os: boolean;
}

const tfcDiagnosticReportBezoekAanSpecialistOptions = [
    TfcV1DiagnosticReportBezoekAanSpecialist.ja,
    TfcV1DiagnosticReportBezoekAanSpecialist.nee,
];
const advisedPolicies = [
    Policies.controleVolgensRichtlijn,
    Policies.verwijzingOogartsDoorHuisarts,
    Policies.verwijzingOogartsDoorOptometrist,
    Policies.vervolgonderzoekOptometrist,
    Policies.anders,
];

export function TfcV1DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    return <InnerTfcV1DiagnosticReportAddEdit {...props} />;
}

class InnerTfcV1DiagnosticReportAddEdit extends Component<
    ICustomControlDiagnosticReportAddEditProps,
    IInnerTfcV1DiagnosticReportAddEditState
> {
    public render() {
        return (
            <ProcessContainer isActiveActivity={this.props.itemState.initialIsOpen}>
                <ProcessItemHeader
                    date={this.props.process && this.props.process.dateSent}
                    isFirstItem={this.props.isFirstItem}
                    isItemOpen
                    organizationName={this.props.currentDiagnosticReport?.performerOrganization?.name}
                    practitionerName={
                        this.props.currentDiagnosticReport && this.props.currentDiagnosticReport.performerPractitioner
                            ? getDisplayName(this.props.currentDiagnosticReport.performerPractitioner)
                            : undefined
                    }
                    process={this.props.process}
                    title={translateKey('diagnosticReport.category_consultReport')}
                />

                <Form onSubmit={() => null}>
                    {this.addEditConsultReportForm()}

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={this.props.onCancel}
                                entity={ProcessEntity.diagnosticReport}
                                isExisting={!!this.props.request.id}
                                serviceCode={this.props.service.code}
                                serviceType={this.props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={this.state.buttonDisabled}
                                save={async () => {
                                    await this.saveDiagnosticReport('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={this.state.buttonDisabled}
                                saveSend={async () => {
                                    await this.saveDiagnosticReport('final');
                                }}
                            />,
                        ]}
                    />
                </Form>
            </ProcessContainer>
        );
    }

    private addEditConsultReportForm() {
        return (
            <>
                <FormContextProvider
                    value={{
                        placeholderGroupName: 'diagnosticReport',
                        item: this.props.diagnosticReport.customControlData,
                        errors: this.props.errors,
                        onChange: async (key, value) => await this.props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await this.props.validateOneCustomControl(key),
                    }}
                >
                    <FormContextProvider
                        value={{
                            placeholderGroupName: 'diagnosticReport',
                            item: this.props.diagnosticReport.customControlData.bevindingen,
                            errors: this.props.errors,
                            onChange: async (key, value) => await this.handleChangeBevindingen(key, value),
                            onDoValidate: async () => await this.props.validateOneCustomControl('bevindingen'),
                        }}
                    >
                        <HorizontalFormGroup
                            hasErrors={
                                this.props.errors.has('bevindingen') ||
                                this.props.errors.has('customControlData.bevindingen.andersText')
                            }
                        >
                            <FormLabel isRequired>Bevindingen</FormLabel>
                            <InputContainer>
                                {this.bevindingenOption('niet_te_beoordelen', 'Niet te beoordelen')}
                                {this.bevindingenOption('geen_afwijkingen', 'Geen afwijkingen')}
                                {this.bevindingenOption('exsudaten', 'Exsudaten')}
                                {this.bevindingenOption('bloedinkjes', 'Bloedinkjes en microaneurysma')}
                                {this.bevindingenOption('cotton_wool_spots', 'Cotton wool spots')}
                                {this.bevindingenOption('andersText', 'Anders, namelijk...')}
                            </InputContainer>
                        </HorizontalFormGroup>
                    </FormContextProvider>

                    <DiagnosisConclusionSelection
                        conclusie_diagnose={this.props.diagnosticReport.customControlData.conclusie_diagnose}
                        diagnosis={this.props.diagnosticReport.diagnosis}
                        errors={this.props.errors}
                        handleChangeConclusieDiagnose={this.props.handleChangeCustomControl}
                        handleChangeDiagnosisConsideration={async (diagnosis) =>
                            await this.props.handleChange('diagnosis', diagnosis)
                        }
                        isRequired
                        service={this.props.service}
                        validateConclusieDiagnose={this.props.validateOneCustomControl}
                        validateDiagnosisConsideration={async () => this.props.validateOne('diagnosis')}
                    />

                    <FormDivider />

                    <HeaderDivider text="Advies" />

                    <OldFundusscreeningPolicyAndUrgencyAddEditFields
                        advisedPolicies={advisedPolicies}
                        customControlData={this.props.diagnosticReport.customControlData}
                        errors={this.props.errors}
                        handleChangeCustomControl={async (values) => {
                            if (
                                [
                                    Policies.verwijzingOogartsDoorHuisarts,
                                    Policies.verwijzingOogartsDoorOptometrist,
                                ].includes(values.geadviseerd_beleid as Policies)
                            ) {
                                await this.props.handleChangeCustomControl({
                                    ...values,
                                    bezoek_aan_specialist_nu_nodig: TfcV1DiagnosticReportBezoekAanSpecialist.ja,
                                });
                            } else if (
                                [Policies.controleVolgensRichtlijn, Policies.vervolgonderzoekOptometrist].includes(
                                    values.geadviseerd_beleid as Policies
                                )
                            ) {
                                await this.props.handleChangeCustomControl({
                                    ...values,
                                    bezoek_aan_specialist_nu_nodig: TfcV1DiagnosticReportBezoekAanSpecialist.nee,
                                });
                            } else if ([null, Policies.anders].includes(values.geadviseerd_beleid as Policies | null)) {
                                await this.props.handleChangeCustomControl({
                                    ...values,
                                    bezoek_aan_specialist_nu_nodig: null,
                                });
                            } else {
                                await this.props.handleChangeCustomControl(values);
                            }
                        }}
                    />

                    <RadioGroupField
                        error={this.props.errors.get('customControlData.bezoek_aan_specialist_nu_nodig')}
                        fieldKey="bezoek_aan_specialist_nu_nodig"
                        hasErrors={this.props.errors.has('customControlData.bezoek_aan_specialist_nu_nodig')}
                        isRequired
                        label={translateKey('diagnosticReport.visitToSpecialistNecessary')}
                        name="bezoek_aan_specialist_nu_nodig"
                        onDoValidate={async () => {
                            await this.props.validateOneCustomControl('geadviseerd_beleid');
                            await this.props.validateOneCustomControl('bezoek_aan_specialist_nu_nodig');
                        }}
                        options={tfcDiagnosticReportBezoekAanSpecialistOptions}
                    />

                    <HorizontalFormGroup hasErrors={this.props.errors.has('remark')}>
                        <FormLabel>Toelichting</FormLabel>
                        <InputContainer>
                            <InputTextArea
                                error={this.props.errors.get('remark')}
                                onChange={async (value) => await this.props.handleChange('remark', value)}
                                onDoValidate={async () => await this.props.validateOne('remark')}
                                placeholder="Een toelichting, opmerkingen of vragen"
                                value={this.props.diagnosticReport.remark}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                </FormContextProvider>
            </>
        );
    }

    private bevindingenOption<K extends keyof IBevindingen>(key: K, label: string) {
        const bevindingen = this.props.diagnosticReport.customControlData.bevindingen;

        return (
            <>
                <div className="row">
                    {key !== 'andersText' && (
                        <div className="col-sm-12 col-xl-8">
                            <InputCheckbox
                                fieldKey={key}
                                text={label}
                            />
                        </div>
                    )}
                    {key === 'andersText' && (
                        <div className="col">
                            <InputTextCheckbox
                                error={this.props.errors.get('customControlData.bevindingen.andersText')}
                                expandableTextArea={true}
                                inputStyle={{ paddingTop: 0 }}
                                labelStyle={{ width: '100%' }}
                                onChange={async (value, checked) =>
                                    await this.handleChangeBevindingen('andersText', value, checked)
                                }
                                onDoValidate={async () => {
                                    await this.props.validateOneCustomControl('bevindingen');
                                    await this.props.validateOneCustomControl('bevindingen.andersText');
                                }}
                                placeholder={label}
                                text={bevindingen.andersText}
                            />
                        </div>
                    )}
                </div>

                {(bevindingen[key] || (key === 'andersText' && this.state.isAndersTextBevindingenChecked)) && (
                    <InputControl indented>
                        <InputCheckbox
                            fieldKey={`${key}_od`}
                            text="OD"
                        />
                        <InputCheckbox
                            fieldKey={`${key}_os`}
                            text="OS"
                        />
                    </InputControl>
                )}
            </>
        );
    }

    private async saveDiagnosticReport(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedDiagnosticReport = await this.props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            await this.setState({ buttonDisabled: false });

            return;
        }

        const diagnosticReportId = await this.props.onSaveDiagnosticReport(saveType);

        if (saveType === 'draft') {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'diagnosticReport',
                translateKey('toast.draftDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                this.props.service,
                'diagnosticReport',
                translateKey('toast.answerSentToOrganizationSuccess', this.props.request.requesterOrganization?.name)
            );
        }

        await this.setState({ buttonDisabled: false });
        await this.props.onSave(diagnosticReportId);
    }

    private async handleChangeBevindingen<K extends keyof IBevindingen>(
        name: K,
        value: IBevindingen[K],
        checked?: boolean
    ) {
        const updateBevindingen = {
            ...this.props.diagnosticReport.customControlData.bevindingen,
            [name]: value,
        };

        let updateConclusieDiagnose = undefined;
        if (
            (updateBevindingen.niet_te_beoordelen && updateBevindingen.geen_afwijkingen) ||
            updateBevindingen.geen_afwijkingen
        ) {
            updateConclusieDiagnose = ConclusieDiagnoseOption.geen_afwijkingen;
        } else if (updateBevindingen.niet_te_beoordelen) {
            updateConclusieDiagnose = ConclusieDiagnoseOption.nog_geen_diagnose;
        }

        if (updateConclusieDiagnose) {
            await this.props.handleChangeCustomControl({
                bevindingen: updateBevindingen,
                conclusie_diagnose: updateConclusieDiagnose,
            });
        } else {
            await this.props.handleChangeCustomControl({
                bevindingen: updateBevindingen,
            });
        }

        if (name === 'andersText' && checked !== undefined && checked !== this.state.isAndersTextBevindingenChecked) {
            await this.setState({ isAndersTextBevindingenChecked: checked });
        }
    }
}

export function tfcV1DiagnosticReportValidateOneCustomControlData(
    name: string,
    customControlData: { bevindingen: IBevindingen },
    errors: ValidationErrors
) {
    if (name === 'bevindingen') {
        return tfcV1DiagnosticReportValidateCustomControlData(customControlData, errors);
    }

    return errors;
}

export function tfcV1DiagnosticReportValidateCustomControlData(
    customControlData: { bevindingen: IBevindingen },
    errors: ValidationErrors
) {
    const bevindingen = customControlData.bevindingen;
    const bevindingenFilledIn =
        bevindingen.niet_te_beoordelen ||
        bevindingen.geen_afwijkingen ||
        bevindingen.exsudaten ||
        bevindingen.bloedinkjes ||
        bevindingen.cotton_wool_spots ||
        (bevindingen.andersText && bevindingen.andersText !== '');

    return bevindingenFilledIn ? errors.remove('bevindingen') : errors.set('bevindingen', 'any_required');
}

export function tfcV1DiagnosticReportInitializeCustomControlData() {
    return {
        bevindingen: {
            niet_te_beoordelen: false,
            niet_te_beoordelen_od: false,
            niet_te_beoordelen_os: false,
            geen_afwijkingen: false,
            geen_afwijkingen_od: false,
            geen_afwijkingen_os: false,
            exsudaten: false,
            exsudaten_od: false,
            exsudaten_os: false,
            bloedinkjes: false,
            bloedinkjes_od: false,
            bloedinkjes_os: false,
            cotton_wool_spots: false,
            cotton_wool_spots_od: false,
            cotton_wool_spots_os: false,
            andersText: null,
            andersText_od: false,
            andersText_os: false,
        },
        conclusie_diagnose: null,
        geadviseerd_beleid: null,
        geadviseerd_beleid_anders: null,
        urgentie: null,
        bezoek_aan_specialist_nu_nodig: null,
    };
}
